import { useCallback, useRef, useState } from "react";

import { useVirtualizer } from "@tanstack/react-virtual";

import {
  useCreateRole,
  useDeleteRole,
  useUpdateUser,
} from "src/adminPanel/queries";
import { Button } from "src/base-components/Button";
import { Checkbox } from "src/base-components/Checkbox";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { OrganizationInDB, User } from "src/clients/taktile-api";

type ChangeRole = {
  userId: string;
  userName: string;
  role: string;
  role_id?: string;
  add: boolean;
};

type RoleGroup = {
  org: OrganizationInDB;
  roles: NonNullable<User["roles"]>;
};

const RoleGroupDisplay: React.FC<{
  orgId: string;
  group: RoleGroup;
  wss: WorkspaceDataplane[];
  onRemoveRole: (user: User, role: { id: string; name: string }) => void;
  user: User;
}> = ({ orgId, group, wss, onRemoveRole, user }) => {
  const renderWorkspaceInfo = (roleArguments: Record<string, string>) => {
    const workspaceId = roleArguments["ws_id"];
    if (!workspaceId) return null;

    const workspace = wss.find((w) => w.id === workspaceId);
    return (
      <p className="!text-gray-600 font-inter-normal-12px">
        Workspace: {workspace ? workspace.name : "N/A (probably deleted)"}
      </p>
    );
  };

  return (
    <div
      key={orgId}
      className="m-2 rounded-md border border-gray-200 border-px border-opacity-60 bg-gray-100 p-2"
    >
      <div className="mb-2">
        <p className="font-inter-medium-13px text-gray-800">{group.org.name}</p>
      </div>
      {group.roles?.map((role) => (
        <div
          key={role.id}
          className="ml-2 flex flex-row justify-between border-gray-200 border-t pt-2"
        >
          <div>
            <p className="font-inter-medium-13px">{role.name}</p>
            {renderWorkspaceInfo(role.arguments ?? {})}
          </div>
          <div className="px-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onRemoveRole(user, role)}
            >
              Remove
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

const UserRow: React.FC<{
  user: User;
  orgs: OrganizationInDB[];
  wss: WorkspaceDataplane[];
  showRoles: boolean;
  onActivateUser: (user: User, activate: boolean) => void;
  onToggleAdmin: (user: User) => void;
  onRemoveRole: (user: User, role: { id: string; name: string }) => void;
}> = ({
  user,
  orgs,
  wss,
  showRoles,
  onActivateUser,
  onToggleAdmin,
  onRemoveRole,
}) => {
  const roleGroups = (user.roles ?? []).reduce(
    (acc, role) => {
      const orgId = role.arguments?.["org_id"];
      if (!orgId) return acc;

      const org = orgs.find((o) => o.id === orgId);
      if (!org) return acc;

      if (!acc[orgId]) {
        acc[orgId] = {
          org,
          roles: [],
        };
      }

      if (!acc[orgId].roles) {
        acc[orgId].roles = [];
      }
      acc[orgId].roles.push(role);
      return acc;
    },
    {} as Record<string, RoleGroup>,
  );

  return (
    <div className="mx-2 flex w-full flex-row border-gray-200 border-b">
      <div className="w-[18%] border-gray-200 border-r px-2 pt-2 pb-1">
        <p className="text-wrap break-words font-inter-normal-12px">
          {user.username}
        </p>
      </div>
      <div className="w-[22%] border-gray-200 border-r px-2 pt-2 pb-1">
        <p className="font-inter-normal-12px">{user.email}</p>
      </div>
      <div className="w-[5%] border-gray-200 border-r px-2 pt-2 pb-1">
        <Checkbox
          checked={user.is_active}
          onChange={() => onActivateUser(user, !user.is_active)}
        />
      </div>
      <div className="w-[5%] border-gray-200 border-r px-2 pt-2 pb-1">
        <Checkbox checked={user.is_superuser} disabled />
      </div>
      <div className="w-[5%] border-gray-200 border-r px-2 pt-2 pb-1">
        <Checkbox
          checked={!!user.roles?.find((r) => r.name === "taktile_d_admin")}
          onChange={() => onToggleAdmin(user)}
        />
      </div>
      <div className="w-[10%] border-gray-200 border-r px-2 pt-2 pb-1">
        {user.roles
          ?.filter((r) => r.name === "fwd_d")
          .map((r) => (
            <div key={r.id}>
              <p className="font-inter-normal-12px">
                {orgs.find((o) => o.id === r.arguments["org_id"])?.name}
              </p>
            </div>
          ))}
      </div>
      <div className="w-[5%] border-gray-200 border-r px-2 pt-2 pb-1">
        {user.roles
          ?.filter((r) => r.name === "fwd_viewer")
          .map((r) => (
            <div key={r.id}>
              <p className="font-inter-normal-12px">
                {orgs.find((o) => o.id === r.arguments["org_id"])?.name}
              </p>
            </div>
          ))}
      </div>
      {showRoles && (
        <div className="w-[25%] px-2 pt-2 pb-1">
          {Object.entries(roleGroups).map(([orgId, group]) => (
            <RoleGroupDisplay
              key={orgId}
              group={group}
              orgId={orgId}
              user={user}
              wss={wss}
              onRemoveRole={onRemoveRole}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const UserSettings: React.FC<{
  users: User[];
  orgs: OrganizationInDB[];
  wss: WorkspaceDataplane[];
}> = ({ users, orgs, wss }) => {
  const createRole = useCreateRole();
  const deleteRole = useDeleteRole();
  const updateUser = useUpdateUser();
  const [showServiceaccounts, setShowServiceaccounts] = useState(false);
  const [showSecretLinkUser, setShowSecretLinkUser] = useState(false);
  const [showWsServiceAccount, setShowWsServiceAccount] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [showRoles, setShowRoles] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [changeRole, setChangeRole] = useState<ChangeRole | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [activationUser, setActivationUser] = useState<{
    activate: boolean;
    user: User;
  } | null>(null);

  const parentRef = useRef<HTMLDivElement>(null);

  const handleActivateUser = useCallback((user: User, activate: boolean) => {
    setShowActivationModal(true);
    setActivationUser({ activate, user });
  }, []);

  const handleToggleAdmin = useCallback((user: User) => {
    if (user.email?.endsWith("@taktile.com")) {
      const adminRole = user.roles?.find((r) => r.name === "taktile_d_admin");
      const isAddingRole = !adminRole;

      setShowConfirmationModal(true);
      setChangeRole({
        userName: user.username ?? "unknown",
        userId: user.id,
        role: "taktile_d_admin",
        role_id: isAddingRole ? undefined : adminRole.id,
        add: isAddingRole,
      });
    } else {
      alert("Only users with taktile.com email can be made admin");
    }
  }, []);

  const handleRemoveRole = useCallback(
    (user: User, role: { id: string; name: string }) => {
      setShowConfirmationModal(true);
      setChangeRole({
        userName: user.username ?? "unknown",
        userId: user.id,
        role: role.name,
        role_id: role.id,
        add: false,
      });
    },
    [],
  );

  const filteredUsers = users
    .filter(
      (u) =>
        showServiceaccounts ||
        !(
          u.username?.startsWith("__service_account__") ||
          u.username?.startsWith("__decide_service_account__")
        ),
    )
    .filter(
      (u) =>
        showSecretLinkUser ||
        !u.username?.startsWith("__taktile-secret-link-user__"),
    )
    .filter(
      (u) =>
        showWsServiceAccount ||
        !u.username?.startsWith("__ws_service_account__"),
    )
    .filter((u) => showInactive || u.is_active)
    .filter((u) => email === "" || u.email?.includes(email))
    .filter((u) => name === "" || u.username?.includes(name));

  const rowVirtualizer = useVirtualizer({
    count: filteredUsers.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 100,
    overscan: 5,
  });

  return (
    <div className="px-1">
      <ConfirmationModal
        description={`Are you sure you want ${
          changeRole?.add ? "add" : "remove"
        } the role ${changeRole?.role} on ${changeRole?.userName}?`}
        open={showConfirmationModal}
        title="Modify Role"
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={async () => {
          if (changeRole) {
            if (changeRole.add) {
              await createRole.mutateAsync({
                user_id: changeRole.userId,
                arguments: {},
                name: changeRole.role,
              });
              setShowConfirmationModal(false);
            } else {
              await deleteRole.mutateAsync(changeRole.role_id!);
              setShowConfirmationModal(false);
            }
          }
        }}
      />
      <ConfirmationModal
        description={`Are you sure you want ${
          activationUser?.activate ? "activate" : "deactivate"
        } the user ${activationUser?.user.username}?`}
        open={showActivationModal}
        title="Modify User"
        onClose={() => setShowActivationModal(false)}
        onConfirm={async () => {
          if (activationUser) {
            const new_user = activationUser.user;
            new_user.is_active = activationUser.activate;
            await updateUser.mutateAsync(new_user);
          }
          setShowActivationModal(false);
        }}
      />
      <div className="mb-4 flex flex-row rounded-md border border-gray-200 bg-gray-200 bg-opacity-30 px-3 py-3">
        <div className="flex flex-col">
          <Label>Filter Email</Label>
          <div className="mt-1 mr-8 flex w-full flex-row items-center gap-x-4">
            <div className="flex flex-row items-center">
              <Checkbox
                checked={showServiceaccounts}
                className="mr-1"
                name="show_serviceaccounts"
                onChange={() => setShowServiceaccounts(!showServiceaccounts)}
              />
              <p className="inline-block font-inter-semibold-13px text-gray-800">
                Show Serviceaccounts
              </p>
            </div>
            <div className="flex flex-row items-center">
              <Checkbox
                checked={showSecretLinkUser}
                className="mr-1"
                name="show_secret_link_user"
                onChange={() => setShowSecretLinkUser(!showSecretLinkUser)}
              />
              <p className="inline-block font-inter-semibold-13px text-gray-800">
                Show Secret Link User
              </p>
            </div>
            <div className="flex flex-row items-center">
              <Checkbox
                checked={showWsServiceAccount}
                className="mr-1"
                name="show_ws_secret_link_user"
                onChange={() => setShowWsServiceAccount(!showWsServiceAccount)}
              />
              <p className="inline-block font-inter-semibold-13px text-gray-800">
                Show WS Service Account
              </p>
            </div>
            <div className="flex flex-row items-center">
              <Checkbox
                checked={showInactive}
                className="mr-1"
                name="show_inactive"
                onChange={() => setShowInactive(!showInactive)}
              />
              <p className="inline-block font-inter-semibold-13px text-gray-800">
                Show Inactive Users
              </p>
            </div>
            <div className="flex flex-row items-center">
              <Checkbox
                checked={showRoles}
                className="mr-1"
                name="show_roles"
                onChange={() => setShowRoles(!showRoles)}
              />
              <p className="inline-block font-inter-semibold-13px text-gray-800">
                Show Roles
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-row items-center gap-x-4">
          <div className="items-left flex flex-col gap-y-0">
            <Label>Filter Email</Label>
            <Input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="items-left flex flex-col gap-y-0">
            <Label>Filter Name</Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div ref={parentRef} className="h-[calc(100vh-200px)] overflow-auto">
        <div className="table-auto rounded-lg border border-gray-50 bg-white py-2 shadow-base">
          <div>
            <div className="sticky top-0 z-10 flex flex-row bg-white font-inter-medium-12px leading-right">
              <div className="w-[18%]">
                <p className="px-4 py-2">Username</p>
              </div>
              <div className="w-[22%]">
                <p className="px-4 py-2">Email</p>
              </div>
              <div className="w-[5%]">
                <p className="px-4 py-2">Active</p>
              </div>
              <div className="w-[5%]">
                <p className="px-4 py-2">Super User</p>
              </div>
              <div className="w-[5%]">
                <p className="px-4 py-2">Taktile Admin</p>
              </div>
              <div className="w-[10%]">
                <p className="px-4 py-2">FWD</p>
              </div>
              <div className="w-[5%]">
                <p className="px-4 py-2">FWD Viewer</p>
              </div>
              {showRoles && (
                <div className="w-[25%]">
                  <p className="px-4 py-2">Roles</p>
                </div>
              )}
            </div>
          </div>
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const user = filteredUsers[virtualRow.index];
              return (
                <div
                  key={user.id}
                  ref={rowVirtualizer.measureElement}
                  data-index={virtualRow.index}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <UserRow
                    orgs={orgs}
                    showRoles={showRoles}
                    user={user}
                    wss={wss}
                    onActivateUser={handleActivateUser}
                    onRemoveRole={handleRemoveRole}
                    onToggleAdmin={handleToggleAdmin}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
